import { useState, useEffect } from 'react';
import { Users, Car, AlertOctagon, Activity, FileText, Globe, Smartphone, Monitor, Trash2, Database } from 'lucide-react';
import { callRpc } from '../../lib/rpcClient';
import { supabase } from '../../lib/supabase';
import { formatDistanceToNow } from 'date-fns';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { AlertModal } from '../../components/ui/AlertModal';

export function AdminDashboard() {
  const [data, setData] = useState<any>(null);
  const [logins, setLogins] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cleanupResult, setCleanupResult] = useState<any>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{ 
      isOpen: boolean; 
      title: string; 
      message: string;
      confirmLabel?: string;
  }>({ isOpen: false, title: '', message: '' });

  const loadData = async (isManual = false) => {
    try {
      if (isManual) setRefreshing(true);
      else setLoading(true);
      
      const snapshot = await callRpc('get_admin_system_snapshot');
      setData(snapshot);
      
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      const loginEvents = await callRpc('get_admin_login_events', {
        p_from: yesterday,
        p_to: tomorrow,
        p_limit: 12
      });
      setLogins(loginEvents);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleManualCleanup = async () => {
    setCleaning(true);
    setCleanupResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const WORKER_URL = 'https://mycarcosts.gtreulands.workers.dev';
      
      const response = await fetch(`${WORKER_URL}/cleanup/r2?limit=500`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`
        }
      });
      
      if (!response.ok) throw new Error('Cleanup trigger failed');
      const result = await response.json();
      setCleanupResult(result.results);
      
      // Reload stats after cleanup
      setTimeout(() => loadData(true), 1500);
    } catch (err: any) {
      setAlertConfig({
        isOpen: true,
        title: 'Cleanup failed',
        message: err.message || 'Unknown error occurred',
        confirmLabel: 'Labi'
      });
    } finally {
      setCleaning(false);
    }
  };

  const confirmCleanup = () => {
    setShowConfirmModal(true);
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-24 gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className="text-slate-500 font-black uppercase text-[10px] tracking-widest">Aggregating vitals...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 text-red-500 p-8 rounded-[32px] w-full text-center font-bold">
        {error}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-8 font-primary pb-12">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-white mb-2">Fleet Snapshot</h1>
          <p className="text-slate-500 font-bold uppercase text-[10px] tracking-[0.2em]">Live system intelligence dashboard.</p>
        </div>
        <button 
          onClick={() => loadData(true)} 
          disabled={refreshing}
          className="bg-slate-900 border border-slate-800 p-3 rounded-2xl text-slate-400 hover:text-white hover:border-slate-700 transition-all shadow-xl active:scale-95 disabled:opacity-50"
        >
          <Activity className={refreshing ? 'animate-spin' : ''} size={20} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Active Users */}
        <div className="bg-slate-900 border border-slate-800 rounded-[32px] p-6 shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform">
             <Activity size={80} />
          </div>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-blue-500/10 text-blue-400 rounded-2xl border border-blue-500/20 shadow-lg shadow-blue-500/5">
              <Activity size={20} />
            </div>
            <h3 className="font-black text-slate-300 uppercase text-[11px] tracking-widest">Active Pulse</h3>
          </div>
          <div className="space-y-3">
            {[ { l: '1h', v: data.active_users_1h, c: 'text-blue-400' }, { l: '24h', v: data.active_users_24h, c: 'text-white' }, { l: '7d', v: data.active_users_7d, c: 'text-slate-400' } ].map(i => (
              <div key={i.l} className="flex justify-between items-end border-b border-slate-800/50 pb-2">
                <span className="text-slate-500 text-xs font-bold uppercase tracking-tighter">Last {i.l}</span>
                <span className={`text-2xl font-black leading-none ${i.c}`}>{i.v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* New Signups */}
        <div className="bg-slate-900 border border-slate-800 rounded-[32px] p-6 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-green-500/10 text-green-400 rounded-2xl border border-green-500/20 shadow-lg shadow-green-500/5">
              <Users size={20} />
            </div>
            <h3 className="font-black text-slate-300 uppercase text-[11px] tracking-widest">Growth</h3>
          </div>
          <div className="mt-2">
              <div className="text-5xl font-black text-white tracking-tighter leading-none mb-2">+{data.new_users_24h}</div>
              <div className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-6">Users last 24h</div>
              <div className="pt-4 border-t border-slate-800 flex justify-between items-center">
                <span className="text-slate-500 text-xs font-bold uppercase">7d Rolling</span>
                <span className="font-black text-green-400">+{data.new_users_7d}</span>
              </div>
          </div>
        </div>

        {/* Vehicles */}
        <div className="bg-slate-900 border border-slate-800 rounded-[32px] p-6 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-amber-500/10 text-amber-400 rounded-2xl border border-amber-500/20 shadow-lg shadow-amber-500/5">
              <Car size={20} />
            </div>
            <h3 className="font-black text-slate-300 uppercase text-[11px] tracking-widest">Vehicles</h3>
          </div>
          <div className="mt-2 text-center sm:text-left">
              <div className="text-5xl font-black text-white tracking-tighter leading-none mb-2">+{data.new_cars_24h}</div>
              <div className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-6">Added last 24h</div>
              <div className="pt-4 border-t border-slate-800 flex justify-between items-center">
                <span className="text-slate-500 text-xs font-bold uppercase">7d Rolling</span>
                <span className="font-black text-amber-400">+{data.new_cars_7d}</span>
              </div>
          </div>
        </div>

        {/* Entries & Files */}
        <div className="bg-slate-900 border border-slate-800 rounded-[32px] p-6 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-purple-500/10 text-purple-400 rounded-2xl border border-purple-500/20 shadow-lg shadow-purple-500/5">
              <FileText size={20} />
            </div>
            <h3 className="font-black text-slate-300 uppercase text-[11px] tracking-widest">Data Flow</h3>
          </div>
          <div className="space-y-4 mt-2">
            <div>
               <div className="text-2xl font-black text-white leading-none">{data.new_log_entries_24h}</div>
               <div className="text-slate-500 text-[9px] font-black uppercase tracking-widest mt-1">Logs 24h</div>
            </div>
            <div className="pt-4 border-t border-slate-800">
               <div className="text-2xl font-black text-purple-400 leading-none">{data.new_attachments_24h}</div>
               <div className="text-slate-500 text-[9px] font-black uppercase tracking-widest mt-1">Files 24h</div>
            </div>
          </div>
        </div>

        {/* Storage & Cleanup */}
        <div className="bg-slate-900 border border-slate-800 rounded-[32px] p-6 shadow-2xl relative overflow-hidden group">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-red-500/10 text-red-400 rounded-2xl border border-red-500/20 shadow-lg shadow-red-500/5">
              <Database size={20} />
            </div>
            <h3 className="font-black text-slate-300 uppercase text-[11px] tracking-widest">Storage Health</h3>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-end border-b border-slate-800/50 pb-2">
              <div className="flex flex-col">
                <span className="text-slate-500 text-[10px] font-black uppercase tracking-tighter">Temp Files</span>
                <span className="text-slate-600 text-[8px] font-bold uppercase tracking-widest">
                  {((data.temp_files_size || 0) / 1024 / 1024).toFixed(1)} MB
                </span>
              </div>
              <span className="text-2xl font-black text-white leading-none">{data.temp_files_count || 0}</span>
            </div>
            <div className="flex justify-between items-end border-b border-slate-800/50 pb-2">
              <span className="text-slate-500 text-[10px] font-black uppercase tracking-tighter">Queue Deletion</span>
              <span className="text-2xl font-black text-red-400 leading-none">{data.deletable_files_count || 0}</span>
            </div>
            
            {cleanupResult && (
              <div className="text-[9px] font-bold text-green-500 bg-green-500/10 px-2 py-1 rounded-lg border border-green-500/20 text-center animate-pulse">
                Cleaned: {cleanupResult.deletable} del, {cleanupResult.temp} temp
              </div>
            )}

            <button
               onClick={confirmCleanup}
               disabled={cleaning}
               className="w-full mt-2 py-2 bg-slate-950 border border-slate-800/50 hover:border-red-500/30 hover:bg-red-500/5 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-red-400 transition-all flex items-center justify-center gap-2 group/btn active:scale-95 disabled:opacity-50"
            >
              <Trash2 size={12} className={cleaning ? 'animate-spin' : 'group-hover/btn:scale-110 transition-transform'} />
              {cleaning ? 'Scrubbing...' : 'Manual Clean Now'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Activity Feed */}
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-slate-900 border border-slate-800 rounded-[40px] p-8 shadow-2xl">
            <h3 className="font-black text-white text-xl mb-6 flex items-center gap-3">
              <Activity className="text-blue-500" size={24} />
              Recent Key Activity
            </h3>
            {data.recent_activity?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.recent_activity.map((act: any, idx: number) => (
                  <div key={idx} className="flex flex-col p-4 rounded-3xl bg-slate-950 border border-slate-800/50 hover:border-slate-700 transition-colors">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-[9px] text-blue-500 font-black uppercase tracking-widest bg-blue-500/10 px-2 py-1 rounded-lg border border-blue-500/20">
                        {act.event_type}
                      </span>
                      <span className="text-slate-600 text-[10px] font-bold">
                        {formatDistanceToNow(new Date(act.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <span className="text-slate-200 font-black text-sm truncate">{act.email}</span>
                  </div>
                ))}
              </div>
            ) : (
               <div className="bg-slate-950/50 border border-slate-800/50 rounded-3xl p-12 text-center text-slate-600 font-bold uppercase text-[10px] tracking-widest">
                 Zero events detected.
               </div>
            )}
          </div>

          {/* Login Activity with Metadata */}
          <div className="bg-slate-900 border border-slate-800 rounded-[40px] p-8 shadow-2xl">
            <h3 className="font-black text-white text-xl mb-6 flex items-center gap-3">
              <Globe className="text-green-500" size={24} />
              Recent Login Attempts
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {logins.length > 0 ? (
                logins.map((evt: any) => {
                  const meta = evt.metadata || {};
                  return (
                    <div key={evt.id} className="p-4 rounded-3xl bg-slate-950 border border-slate-800/80 flex flex-col gap-3 group hover:bg-slate-900/40 transition-all">
                      <div className="flex justify-between items-start">
                        <div className="flex flex-col">
                           <span className="text-white font-black text-sm truncate max-w-[180px]">
                            {evt.user_email || 'anonymous'}
                          </span>
                          <span className="text-[10px] text-slate-600 font-bold">
                            {formatDistanceToNow(new Date(evt.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        <div className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-tighter border ${evt.context === 'login_attempt' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'}`}>
                          {evt.path?.split('/').pop() || 'auth'}
                        </div>
                      </div>

                      {/* Metadata Row */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-3 border-t border-slate-900">
                        {meta.ip && (
                          <div className="flex items-center gap-1.5 text-slate-500 text-[10px] font-bold">
                            <Monitor size={12} className="text-slate-700" />
                            <span className="font-mono">{meta.ip}</span>
                          </div>
                        )}
                        {meta.location && (
                          <div className="flex items-center gap-1.5 text-slate-500 text-[10px] font-bold">
                            <Globe size={12} className="text-slate-700" />
                            {meta.location}
                          </div>
                        )}
                        {meta.browser && (
                          <div className="flex items-center gap-1.5 text-slate-500 text-[10px] font-bold">
                            <Smartphone size={12} className="text-slate-700" />
                            {meta.browser.split(' ').slice(0, 2).join(' ')}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full py-12 text-center text-slate-600 font-bold uppercase text-[10px] tracking-widest">
                  No login history available.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="bg-slate-900 border border-slate-800 rounded-[40px] p-8 shadow-2xl flex flex-col h-full">
          <h3 className="font-black text-white text-xl mb-6 flex items-center gap-3">
            <AlertOctagon className="text-red-500" size={24} />
            Core Stability
          </h3>
          
          <div className="space-y-3 mb-8">
            <div className="flex justify-between items-center p-4 rounded-3xl bg-slate-950 border border-white/5">
              <span className="text-slate-500 text-xs font-black uppercase tracking-widest">5xx Errors (24h)</span>
              <span className={`text-xl font-black ${data.errors_24h > 0 ? 'text-red-400 underline decoration-red-500/50' : 'text-green-500'}`}>
                {data.errors_24h}
              </span>
            </div>
            <div className="flex justify-between items-center p-4 rounded-3xl bg-slate-950 border border-white/5">
              <span className="text-slate-500 text-xs font-black uppercase tracking-widest">Asset 404s (24h)</span>
              <span className={`text-xl font-black ${data.file_404_24h > 0 ? 'text-orange-400' : 'text-green-500'}`}>
                {data.file_404_24h}
              </span>
            </div>
          </div>

          <h4 className="font-black text-white uppercase text-[10px] tracking-widest mb-4 px-1">Unresolved Incident Log</h4>
          <div className="flex-1 overflow-y-auto space-y-3 pr-1 custom-scrollbar">
            {data.latest_errors?.length > 0 ? (
              data.latest_errors.map((err: any) => (
                 <div key={err.id} className="p-4 rounded-3xl bg-red-500/5 border border-red-500/10 group hover:border-red-500/30 transition-all">
                   <div className="flex items-center gap-3 mb-2">
                     <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-lg border ${err.status_code >= 500 ? 'bg-red-500/20 text-red-500 border-red-500/20' : 'bg-orange-500/20 text-orange-500 border-orange-500/20'}`}>
                       {err.status_code || 'ERR'}
                     </span>
                     <span className="text-slate-500 text-[10px] font-bold truncate flex-1">{err.path}</span>
                   </div>
                   <div className="text-slate-300 text-sm font-medium line-clamp-2 leading-snug">
                     {err.message || 'Unknown server exception.'}
                   </div>
                 </div>
              ))
            ) : (
              <div className="text-center text-slate-700 font-bold uppercase text-[10px] tracking-widest py-10">
                System healthy.
              </div>
            )}
          </div>
        </div>

      </div>

      <ConfirmModal
        isOpen={showConfirmModal}
        title="Manual R2 Cleanup"
        message="Trigger manual R2 storage cleanup now? (Processes up to 500 records)"
        confirmLabel="Clean Queue"
        onConfirm={() => {
          setShowConfirmModal(false);
          handleManualCleanup();
        }}
        onCancel={() => setShowConfirmModal(false)}
      />

      <AlertModal
        {...alertConfig}
        onClose={() => setAlertConfig(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
